import type { Lesson } from './courses';

/**
 * Parcours 8 : Data & Analytics — 6 lecons.
 */
export const DATA_ANALYTICS_LESSONS: Lesson[] = [
  {
    slug: 'data-driven',
    title: 'Devenir data-driven',
    duration: '4 min',
    glossaryTerms: ['marketing-dashboard', 'kpi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Etre data-driven signifie prendre ses decisions en se basant sur les donnees plutot que sur l'intuition. Dans un contexte SaaS, cela implique de mesurer systematiquement l'impact de chaque action et de laisser les chiffres guider la strategie.\n\nLes 3 niveaux de maturite data :\n- **Niveau 1 — Reactif** : Vous regardez les donnees quand il y a un probleme\n- **Niveau 2 — Proactif** : Vous consultez les donnees regulierement pour anticiper\n- **Niveau 3 — Predictif** : Vous utilisez les donnees pour predire les tendances et agir en avance",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Les entreprises data-driven ont 23x plus de chances d'acquerir des clients, 6x plus de chances de les retenir, et sont 19x plus rentables (McKinsey). Ce n'est pas un luxe reserve aux grandes entreprises : avec les bons outils, meme une startup de 5 personnes peut etre data-driven.\n\nLa cle : ne pas mesurer tout, mais mesurer ce qui compte. 3 a 5 KPIs bien suivis valent mieux que 50 metriques jamais consultees.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Choisissez vos 3-5 KPIs prioritaires** — Ceux qui mesurent directement la sante de votre business\n2. **Mettez en place un dashboard** — Un ecran unique avec vos metriques cles, visible par toute l'equipe\n3. **Ritualisez la consultation** — 5 minutes chaque matin, 30 minutes chaque semaine\n4. **Posez-vous la question** — \"Qu'est-ce que les donnees me disent ?\" avant chaque decision\n5. **Documentez les apprentissages** — Chaque experience, reussie ou ratee, enrichit votre connaissance",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le **Marketing Dashboard** est votre point d'entree data-driven. Il regroupe les metriques essentielles de vos actions growth : contenus generes, prospects dans le pipeline, taux de referral et performance globale.",
      },
    ],
    linkedModule: {
      label: 'Marketing Dashboard',
      path: '/growth/marketing-dashboard',
      icon: 'BarChart3',
    },
  },
  {
    slug: 'product-kpis',
    title: 'Les KPIs produit essentiels',
    duration: '4 min',
    glossaryTerms: ['marketing-dashboard', 'kpi', 'churn'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Les KPIs produit mesurent l'engagement et la sante de votre produit. Ils vont au-dela des metriques financieres (MRR, churn) pour capturer comment vos utilisateurs interagissent avec votre outil.\n\nKPIs produit essentiels :\n- **DAU / MAU** — Utilisateurs actifs quotidiens / mensuels (mesure l'engagement)\n- **Ratio DAU/MAU** — Aussi appele \"stickiness\" (adhesivite). Un ratio de 20%+ est bon pour un SaaS B2B\n- **Retention Rate** — Pourcentage d'utilisateurs qui reviennent apres 7, 30, 90 jours\n- **Time to Value** — Temps entre l'inscription et le premier moment de valeur percue\n- **Feature Adoption** — Pourcentage d'utilisateurs qui utilisent une fonctionnalite specifique",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Les KPIs financiers (MRR, churn) sont des indicateurs retardes : quand ils baissent, le probleme existe depuis des semaines. Les KPIs produit sont des indicateurs avances : une baisse du DAU predit un futur churn.\n\nLes equipes produit qui suivent les KPIs d'engagement detectent les problemes 2 a 4 semaines avant qu'ils n'impactent les metriques financieres. C'est la difference entre prevenir et guerir.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Definissez ce qu'est un \"utilisateur actif\"** — Quelle action minimum ? (connexion, action metier, etc.)\n2. **Mesurez le DAU et MAU** — Suivez quotidiennement le nombre d'utilisateurs actifs\n3. **Calculez le ratio DAU/MAU** — Un ratio en baisse signale un probleme d'engagement\n4. **Suivez la retention par cohorte** — Comparez les cohortes mois par mois\n5. **Mesurez le Time to Value** — Optimisez l'onboarding pour le reduire",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le **Marketing Dashboard** affiche les metriques d'engagement de vos utilisateurs. Croisez ces donnees avec les feedbacks du **Feedback Inbox** pour comprendre les raisons derriere les chiffres.",
      },
    ],
    linkedModule: {
      label: 'Marketing Dashboard',
      path: '/growth/marketing-dashboard',
      icon: 'BarChart3',
    },
  },
  {
    slug: 'cohort-analysis',
    title: "L'analyse par cohortes",
    duration: '4 min',
    glossaryTerms: ['marketing-dashboard', 'churn', 'kpi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "L'analyse par cohortes (Cohort Analysis) consiste a regrouper les utilisateurs par periode d'inscription et a comparer leur comportement dans le temps. Au lieu de regarder la retention globale (qui melange tous les utilisateurs), vous comparez les cohortes entre elles.\n\nExemple :\n- Cohorte janvier : 100 inscrits, 60 actifs au mois 2, 40 au mois 3\n- Cohorte fevrier : 120 inscrits, 80 actifs au mois 2, 60 au mois 3\n\nLa cohorte fevrier retient mieux — pourquoi ? L'amelioration de l'onboarding en janvier a porte ses fruits.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "La retention globale peut etre trompeuse : si vous acquérez beaucoup de nouveaux utilisateurs, le pourcentage global masque un churn eleve des anciens. L'analyse par cohortes revele la vraie tendance.\n\nElle permet aussi de mesurer l'impact de chaque changement produit. Si la retention de la cohorte mars est meilleure que celle de fevrier, c'est probablement grace aux ameliorations deployees en mars.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Definissez vos cohortes** — Par mois d'inscription, par source d'acquisition, ou par plan tarifaire\n2. **Mesurez la retention** — Pourcentage d'utilisateurs actifs a J+7, J+30, J+60, J+90\n3. **Comparez les cohortes** — Identifiez les tendances : la retention s'ameliore-t-elle au fil du temps ?\n4. **Correlez avec les changements** — Quel changement produit a impacte la retention ?\n5. **Fixez des objectifs** — Ex: chaque nouvelle cohorte doit avoir une retention J+30 superieure a la precedente",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le **Marketing Dashboard** permet de visualiser vos metriques dans le temps. Croisez les periodes pour identifier les tendances. Les feedbacks du **Feedback Inbox** apportent le contexte qualitatif derriere les chiffres quantitatifs.",
      },
    ],
    linkedModule: {
      label: 'Marketing Dashboard',
      path: '/growth/marketing-dashboard',
      icon: 'BarChart3',
    },
  },
  {
    slug: 'trends-detection',
    title: 'Detecter les tendances',
    duration: '3 min',
    glossaryTerms: ['marketing-dashboard', 'feedback-inbox', 'kpi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "La detection de tendances (Trend Detection) consiste a identifier les changements significatifs dans vos metriques avant qu'ils ne deviennent des problemes (ou des opportunites manquees).\n\nTypes de tendances :\n- **Tendance haussiere** — Un KPI s'ameliore progressivement (bonne nouvelle, a amplifier)\n- **Tendance baissiere** — Un KPI se degrade progressivement (alerte, a investiguer)\n- **Anomalie** — Un pic ou un creux soudain (a comprendre rapidement)\n- **Saisonnalite** — Des variations previsibles liees au calendrier (a anticiper)",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Detecter une tendance baissiere 2 semaines a l'avance vous donne le temps de reagir. La detecter 2 mois trop tard vous met en mode pompier. La proactivite data est ce qui separe les equipes performantes des equipes en reaction permanente.\n\nLes anomalies, positives ou negatives, sont aussi des sources d'apprentissage. Un pic inexplique de signups peut reveler un canal d'acquisition que vous n'aviez pas identifie.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Definissez des seuils d'alerte** — Ex: churn > 5%, DAU en baisse de 10% sur 7 jours\n2. **Consultez les dashboards quotidiennement** — Meme 2 minutes suffisent pour reperer une anomalie\n3. **Comparez semaine par semaine** — Les variations jour a jour sont souvent du bruit\n4. **Investiguez les anomalies** — Croisez les donnees quantitatives avec les feedbacks qualitatifs\n5. **Documentez les tendances** — Creez un historique des tendances detectees et des actions prises",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le **Marketing Dashboard** affiche vos metriques avec des tendances visuelles. Le **Feedback Inbox** complete l'analyse en revelant les raisons qualitatives derriere les mouvements de vos KPIs.",
      },
    ],
    linkedModule: {
      label: 'Marketing Dashboard',
      path: '/growth/marketing-dashboard',
      icon: 'BarChart3',
    },
  },
  {
    slug: 'weekly-reports',
    title: 'Les rapports hebdomadaires IA',
    duration: '3 min',
    glossaryTerms: ['marketing-dashboard', 'kpi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Le rapport hebdomadaire (Weekly Report) est un resume structure des performances de la semaine ecoulee. Il repond a 3 questions essentielles :\n\n- **Qu'est-ce qui a marche ?** — Les actions qui ont genere des resultats positifs\n- **Qu'est-ce qui n'a pas marche ?** — Les initiatives qui n'ont pas performe\n- **Quelles actions pour la semaine prochaine ?** — Les prochaines etapes prioritaires\n\nAugmente par l'IA, le rapport hebdomadaire peut etre genere automatiquement a partir de vos donnees, vous faisant gagner des heures de compilation manuelle.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Le rapport hebdomadaire force la reflexion et l'alignement. Il empeche les semaines de passer sans que personne ne se demande \"est-ce qu'on progresse ?\". C'est aussi un outil de communication : il aligne l'equipe sur les priorites et les resultats.\n\nLes equipes qui font un bilan hebdomadaire atteignent leurs objectifs trimestriels 40% plus souvent que celles qui n'en font pas (Weekdone).",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Choisissez un jour fixe** — Le vendredi ou le lundi sont les plus courants\n2. **Listez les KPIs de la semaine** — Evolution par rapport a la semaine precedente\n3. **Resumez les actions cles** — Ce qui a ete lance, publie, teste\n4. **Identifiez les tops et flops** — Les 3 meilleures et 3 pires performances\n5. **Planifiez la semaine suivante** — 3-5 actions prioritaires maximum\n6. **Partagez avec l'equipe** — Transparence et alignement",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le **Marketing Dashboard** fournit les donnees necessaires a votre rapport hebdomadaire : contenus publies, prospects generes, taux de conversion du referral. Utilisez ces donnees pour alimenter votre bilan de la semaine.",
      },
    ],
    linkedModule: {
      label: 'Marketing Dashboard',
      path: '/growth/marketing-dashboard',
      icon: 'BarChart3',
    },
  },
  {
    slug: 'your-analytics',
    title: 'Dashboard & Analytics dans FO',
    duration: '3 min',
    glossaryTerms: ['marketing-dashboard', 'growth-dashboard', 'kpi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Feedback Orchestrator propose deux niveaux d'analytics :\n\n- **Growth Dashboard** — Vue d'ensemble de la plateforme : utilisateurs, organisations, feedbacks, projets. C'est le monitoring global.\n- **Marketing Dashboard** — Focus sur les performances growth : contenus, prospects, referral, revenue estime. C'est votre cockpit marketing.\n\nCombines, ces deux dashboards vous donnent une vision complete de votre business, du niveau macro (growth) au niveau operationnel (marketing).",
      },
      {
        type: 'methodology',
        title: "Comment les utiliser ?",
        content:
          "1. **Growth Dashboard — Vue hebdomadaire** — Consultez une fois par semaine pour le bilan global\n2. **Marketing Dashboard — Vue quotidienne** — Consultez chaque matin pour piloter vos actions\n3. **Identifiez les tendances** — Comparez semaine par semaine, mois par mois\n4. **Croisez les donnees** — Si le MRR baisse, regardez le churn. Si le churn monte, regardez les feedbacks.\n5. **Agissez sur les insights** — Chaque donnee doit mener a une action concrete\n6. **Partagez avec l'equipe** — Un dashboard n'est utile que s'il est consulte",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Rendez-vous sur le **Marketing Dashboard** pour votre pilotage quotidien et le **Growth Dashboard** pour la vision d'ensemble. Chaque metrique est cliquable et mene au module correspondant pour agir directement.",
      },
    ],
    linkedModule: {
      label: 'Marketing Dashboard',
      path: '/growth/marketing-dashboard',
      icon: 'BarChart3',
    },
  },
];
